import { Component, OnInit, OnDestroy } from '@angular/core';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from '../../../core/service/auth.service';
import { AuthUser } from '@model';
import { UserProfileService } from '../../../core/service/user-profile.service';
import { TimezonesService } from '../../../core/service/timezones.service';
import { GlobalAlertService } from '../../../core/service/global-alert.service';
import { Timezone } from '@model';

@Component({
  selector: 'optima-user-menu',
  templateUrl: './user-menu.component.html',
  styleUrls: ['./user-menu.component.scss'],
  animations: [
    trigger('slideInOut', [
      state('void', style({ right: '-400px' })),
      state('*', style({ right: '0' })),
      transition('void <=> *', animate('300ms cubic-bezier(0.4, 0, 0.2, 1)'))
    ]),
    trigger('fadeInOut', [
      state('void', style({ opacity: 0 })),
      state('*', style({ opacity: 1 })),
      transition('void <=> *', animate('200ms ease-in-out'))
    ])
  ]
})
export class UserMenuComponent implements OnInit, OnDestroy {
  isMenuOpen = false;
  user: AuthUser | null = null;
  userProfile: any = null;
  navbarHeight = 0;
  isEditMode = false;
  editablePreferences: any = {};
  availableTimezones: Timezone[] = [];
  measurementOptions = ['METRIC', 'IMPERIAL'];
  private destroy$ = new Subject<void>();

  constructor(
    private authService: AuthService,
    private userProfileService: UserProfileService,
    private timezonesService: TimezonesService,
    private translate: TranslateService,
    private globalAlertService: GlobalAlertService
  ) {}

  ngOnInit(): void {
    this.calculateNavbarHeight();
    this.loadTimezones();

    this.authService.user$
      .pipe(takeUntil(this.destroy$))
      .subscribe(user => {
        this.user = user;
        if (user) {
          this.loadUserProfile();
        }
      });
  }

  private loadTimezones(): void {
    if (this.timezonesService.timezones && this.timezonesService.timezones.length > 0) {
      this.availableTimezones = this.timezonesService.timezones;
    } else {
      this.timezonesService.loadTimezones()
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          timezones => {
            this.availableTimezones = timezones;
          },
          error => {
            console.error('💥 [USER-MENU] Error loading timezones:', error);
          }
        );
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private calculateNavbarHeight(): void {
    setTimeout(() => {
      const navbar = document.querySelector('global-nav');
      if (navbar) {
        this.navbarHeight = (navbar as HTMLElement).clientHeight;
      } else {
        this.navbarHeight = 60;
        console.warn('⚠️ [USER-MENU] Navbar not found, using default height:', this.navbarHeight, 'px');
      }
    }, 0);
  }

  private loadUserProfile(): void {
    // Try to use cached profile from AuthService first
    const cachedUser = this.authService.getUserProfileFromCache();
    if (cachedUser) {
      this.userProfile = this.buildProfileFromAuthUser(cachedUser);
      return;
    }

    this.userProfileService.getUserProfile()
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        profile => {
          this.userProfile = profile;
        },
        error => {
          console.error('💥 [USER-MENU] Error loading profile:', error);
          this.userProfile = this.getBasicProfile();
        }
      );
  }

  private buildProfileFromAuthUser(user: AuthUser): any {
    return {
      username: this.buildUsername(user),
      email: user.email,
      name: user.fullName || user.name,
      firstName: user.firstName,
      lastName: user.lastName,
      title: user.userRole || 'User',
      role: user.userRole,
      locale: user.preferences?.locale || 'en-US',
      timezone: user.preferences?.timeZone || this.getBrowserTimezone(),
      measurement: user.preferences?.systemMeasurementUnit || 'METRIC',
      homeApp: 'Optima'
    };
  }

  private buildUsername(user: AuthUser): string {
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    if (user.fullName) {
      return user.fullName;
    }
    return user.username || user.email;
  }

  private getBasicProfile(): any {
    if (!this.user) return null;

    return {
      username: this.user.username || this.user.email,
      email: this.user.email,
      name: this.user.name || 'Unknown User',
      title: this.translate.instant('user.sheet.title'),
      locale: this.getLocaleDisplay(),
      timezone: this.getTimezoneDisplay(),
      measurement: 'Imperial',
      homeApp: 'Optima'
    };
  }

  getUserInitials(): string {
    if (!this.user) return '?';
    
    if (this.user.firstName && this.user.lastName) {
      return (this.user.firstName[0] + this.user.lastName[0]).toUpperCase();
    }
    
    const name = this.user.fullName || this.user.name || this.user.username || this.user.email;
    const parts = name.split(' ');
    
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    
    return name.substring(0, 2).toUpperCase();
  }

  getDisplayName(): string {
    if (!this.user) return 'Unknown User';
    
    if (this.user.fullName) {
      return this.user.fullName;
    }
    
    if (this.user.firstName && this.user.lastName) {
      return `${this.user.firstName} ${this.user.lastName}`;
    }
    
    return this.user.name || this.user.username || 'Unknown User';
  }

  getUsername(): string {
    return this.userProfile?.username || this.user?.username || this.user?.email || 'unknown';
  }

  getEmail(): string {
    return this.userProfile?.email || this.user?.email || 'no-email@example.com';
  }

  getTitle(): string {
    return this.userProfile?.title || this.userProfile?.role || this.translate.instant('user.sheet.title');
  }

  getLocaleDisplay(): string {
    const locale = this.userProfile?.locale || this.getCookieLocale();
    
    const localeMap: { [key: string]: string } = {
      'en_US': 'English (US)',
      'en_GB': 'English (UK)',
      'es_ES': 'Spanish (Spain)',
      'es_MX': 'Spanish (Mexico)',
      'fr_FR': 'French (France)',
      'de_DE': 'German (Germany)',
      'it_IT': 'Italian (Italy)',
      'pt_BR': 'Portuguese (Brazil)'
    };

    return localeMap[locale] || locale || 'English (US)';
  }

  getTimezoneDisplay(): string {
    const timezone = this.userProfile?.timezone || this.getBrowserTimezone();
    
    const timezoneMap: { [key: string]: string } = {
      'America/New_York': '(GMT-05:00) Eastern Time (US & Canada)',
      'America/Chicago': '(GMT-06:00) Central Time (US & Canada)',
      'America/Denver': '(GMT-07:00) Mountain Time (US & Canada)',
      'America/Los_Angeles': '(GMT-08:00) Pacific Time (US & Canada)',
      'Europe/London': '(GMT+00:00) London',
      'Europe/Paris': '(GMT+01:00) Paris',
      'Europe/Madrid': '(GMT+01:00) Madrid',
      'America/Mexico_City': '(GMT-06:00) Mexico City'
    };

    return timezoneMap[timezone] || timezone || '(GMT-05:00) Eastern Time (US & Canada)';
  }

  getMeasurement(): string {
    return this.userProfile?.measurement || 'Imperial';
  }

  getHomeApp(): string {
    return this.userProfile?.homeApp || 'Optima';
  }

  toggleMenu(): void {
    this.isMenuOpen = !this.isMenuOpen;
  }

  closeMenu(): void {
    this.isMenuOpen = false;
  }

  handleKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape' || event.key === 'Esc') {
      this.closeMenu();
      event.preventDefault();
    }
  }

  logout(): void {
    this.closeMenu();
    
    // Use AuthService logout method to ensure proper cleanup
    this.authService.logout();
  }

  private getCookieLocale(): string {
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'locale') {
        return value;
      }
    }
    return 'en_US';
  }

  private getBrowserTimezone(): string {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch (e) {
      return 'America/New_York';
    }
  }

  enterEditMode(): void {
    this.isEditMode = true;
    this.editablePreferences = {
      timeZone: this.userProfile?.timezone || this.getBrowserTimezone(),
      systemMeasurementUnit: this.userProfile?.measurement || 'METRIC'
    };
  }

  cancelEdit(): void {
    this.isEditMode = false;
    this.editablePreferences = {};
  }

  savePreferences(): void {
    if (!this.user || !this.user.id) {
      console.error('💥 [USER-MENU] Cannot save: No user ID');
      return;
    }

    const updatePayload = {
      preferences: {
        timeZone: this.editablePreferences.timeZone,
        systemMeasurementUnit: this.editablePreferences.systemMeasurementUnit
      }
    };

    this.userProfileService.updatePreferences(this.user.id, updatePayload)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        response => {
          if (this.userProfile) {
            this.userProfile.timezone = this.editablePreferences.timeZone;
            this.userProfile.measurement = this.editablePreferences.systemMeasurementUnit;
          }
          if (this.user && this.user.preferences) {
            this.user.preferences.timeZone = this.editablePreferences.timeZone;
            this.user.preferences.systemMeasurementUnit = this.editablePreferences.systemMeasurementUnit;
          }
          this.timezonesService.userTimezone = this.editablePreferences.timeZone;
          this.isEditMode = false;
          
          const successMessage = this.translate.instant('user.preferences.update.success');
          this.globalAlertService.setSuccess(successMessage);
        },
        error => {
          const errorMessage = this.translate.instant('user.preferences.update.error');
          this.globalAlertService.setError(errorMessage);
        }
      );
  }

  getTimezoneDisplayLabel(timezoneName: string): string {
    const timezone = this.availableTimezones.find(tz => tz.timezoneName === timezoneName);
    return timezone?.displayLabel || timezoneName;
  }
}
